import React from "react";
import PropTypes from "prop-types";
import { FormRow, FormInput } from "./Styles";
import styled, { css } from "styled-components";
import { Body1 } from "@arcteryx/components-typography";

const Label = styled.label`
  font-family: var(--font-helvetica);
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  white-space: pre-wrap;

  ${props =>
    !props.disabled &&
    css`
      &:hover {
        span {
          color: var(--black);
        }
      }
    `}

  ${props =>
    props.checked &&
    css`
      span {
        color: var(--black);
      }
    `}
  ${props =>
    props.disabled &&
    css`
      span {
        cursor: not-allowed;
        color: var(--inactive-state-grey);
      }
    `};
`;
const Toggle = styled.span`
  position: relative;
  cursor: pointer;
  margin: 0 5px;
  display: flex;
  span {
    padding: 0 0.25rem;
  }
  &:before,
  &:after {
    content: "";
    display: block;
    background: var(--white);
  }
  &:before {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-top: 0.25rem;
  }
  &:after {
    position: absolute;
    border: 1px solid var(--black);
    border-radius: 50%;
    background: var(--white);
    background-position: center;
    top: 0;
    height: 15px;
    width: 15px;
    margin-top: 0.25rem;
  }
`;
const Radio = styled.input.attrs({ type: "radio" })`
  position: absolute;
  opacity: 0;
  cursor: not-allowed;
  &:focus + ${Toggle} {
    &::before {
      z-index: 1;
      background: none;
    }
  }

  ${props =>
    props.disabled &&
    css`
      &:hover + ${Toggle}:after {
        box-shadow: none !important;
      }
      & + ${Toggle}:after {
        border: 1px solid var(--inactive-state-grey);
      }
    `};
  ${props =>
    props.checked &&
    css`
      & + ${Toggle}:before {
        border: 1px solid var(--black);
      }
      & + ${Toggle}:after {
        position: absolute;
        width: 12px;
        height: 12px;
        top: 2px;
        left: 2px;
        background: var(--black);
      }
    `};
  ${props =>
    props.disabled &&
    props.checked &&
    css`
      & + ${Toggle}:before {
        border: 1px solid var(--inactive-state-grey);
      }
      & + ${Toggle}:after {
        border: 1px solid var(--inactive-state-grey);
        background: var(--inactive-state-grey);
      }
    `};
`;

const RadioButton = React.forwardRef((props, ref) => {
  const onClickHandler = e => {
    try {
      if (!props.disabled && props.isGroup) {
        // If part of a group send value to consuming app to handle change
        props.handleClick(e.target.value);
      } else if (!props.disabled) {
        // If individual radio then toggle checked onClick
        props.handleClick(!props.checked);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const id = (props.id || props.value || props.labelText).replace(/ /g, "");

  return (
    <FormRow className={`qa-form-row ${props.className ? props.className : ""}`}>
      <FormInput className={`qa-form-radio`}>
        <Label
          className="qa-control-label"
          htmlFor={props.id || props.name}
          disabled={props.disabled}
          checked={props.checked}
        >
          <Radio
            ref={ref}
            className="qa-form-control"
            name={props.name}
            id={id}
            value={props.value || props.labelText}
            aria-labelledby={`labelFor--${props.id || props.name}`}
            disabled={props.disabled}
            checked={props.checked}
            onClick={onClickHandler}
          />
          <Toggle>
            <Body1 tag="span">{props.labelText}</Body1>
          </Toggle>
        </Label>
      </FormInput>
    </FormRow>
  );
});

RadioButton.displayName = "RadioButton";

RadioButton.propTypes = {
  name: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.string,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  handleClick: PropTypes.func,
  isGroup: PropTypes.bool,
  className: PropTypes.string,
};
RadioButton.defaultProps = {
  disabled: false,
  isGroup: false,
};
export default RadioButton;
